import React, { useState, useEffect } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import NumericKeypad from '@/components/features/shared/NumericKeypad';
import OTPInput from '@/components/features/shared/OTPInput';
import { useParcelData } from '@/hooks/useParcelData';
import { useLockerAssignment } from '@/hooks/useLockerAssignment';
import LockerMonitor from '@/components/features/shared/LockerMonitor';
import { verifyPhone, sendOTP, verifyOTP, fetchLockerAssignment, confirmPayment, completeCollection } from '@/services';
import { useTranslation } from 'react-i18next';
import { checkPaymentStatus } from '@/services/api';
import PaymentMethodSelection from '@/components/features/shared/PaymentMethodSelection';
import { Loader } from 'lucide-react';
import PaymentTerminal from '../shared/PaymentTerminal';

const CollectParcel: React.FC = () => {
    const {
        step,
        setStep,
        setError,
        phone,
        setPhone,
        otp,
        setOTP,
        amount,
        setAmount,
        assignedLocker,
        setAssignedLocker,
        assignmentId,
        setAssignmentId,
    } = useAppContext();

    const { t } = useTranslation();
    const { parcelData, fetchParcelData } = useParcelData();
    const [isLockerClosed, setIsLockerClosed] = useState(false);
    const [isCollectionComplete, setIsCollectionComplete] = useState(false);
    const [paymentQRCode, setPaymentQRCode] = useState('');
    const [paymentUrl, setPaymentUrl] = useState('');
    const [isPollingPayment, setIsPollingPayment] = useState(false);
    const [paymentId, setPaymentId] = useState<string>('');
    const POLLING_INTERVAL = 3000; // Poll every 3 seconds
    const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'pos' | null>(null);
    const [isPreparingPayment, setIsPreparingPayment] = useState(false);
    const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
    const [isTerminalPaymentComplete, setIsTerminalPaymentComplete] = useState(false);

    const handleTerminalPaymentComplete = async () => {
        try {
            if (!assignmentId) {
                setError(t('noActiveAssignment'));
                return;
            }
            const success = await confirmPayment(assignmentId);
            if (success) {
                setStep(13); // Move to collection screen after payment
            } else {
                setError(t('paymentConfirmationFailed'));
            }
        } catch (error) {
            setError(t('paymentConfirmationError'));
        }
    };

    const handlePaymentMethodSelect = async (method: 'paypal' | 'pos') => {
        setPaymentMethod(method);
        setIsPreparingPayment(true);
        
        try {
            if (method === 'paypal') {
                setStep(122); // PayPal payment screen
                setIsPollingPayment(true);
            } else {
                // POS terminal flow
                setStep(123); // New step for POS terminal
            }
        } catch (error) {
            setError(t('paymentInitializationError'));
        } finally {
            setIsPreparingPayment(false);
        }
    };
    
    // Add new handler for PayPal payment initiation
    const handlePayPalLinkClick = () => {
        setIsPollingPayment(true);
        window.open(paymentUrl, '_blank');
    };

    useEffect(() => {
        let pollingInterval: NodeJS.Timeout;
    
        const pollPaymentStatus = async () => {
          if (!paymentId || !isPollingPayment) return;
    
          try {
            const isComplete = await checkPaymentStatus(paymentId);
            if (isComplete) {
              setIsPollingPayment(false);
              // Payment is complete, proceed with collection
              await handlePaymentConfirmation();
            }
          } catch (error) {
            console.error('Error checking payment status:', error);
            setError(t('paymentCheckError'));
          }
        };
    
        if (isPollingPayment) {
          pollingInterval = setInterval(pollPaymentStatus, POLLING_INTERVAL);
        }
    
        return () => {
          if (pollingInterval) {
            clearInterval(pollingInterval);
          }
        };
      }, [isPollingPayment, paymentId]);

    const handleCollectionPhoneEnter = async () => {
        try {
            const isValid = await verifyPhone(phone, '');
            if (isValid) {
                await fetchParcelData(phone);
                await sendOTP(phone);
                setStep(12);
            } else {
                setError('No parcels for this phone registered here');
            }
        } catch (error) {
            setError('An error occurred. Please try again.');
        }
    };
    const handleOTPEnterCollection = async () => {
        setIsVerifyingOTP(true); // Start loading
        try {
            const isValid = await verifyOTP(phone, otp);
            if (isValid) {
                const response = await fetchLockerAssignment(phone);
                
                setAssignedLocker(response.lockerId);
                setAmount(response.amount || 0);
                setAssignmentId(response.assignmentId);
    
                // If response indicates the parcel is already paid or payment is not required,
                // skip payment step and go directly to collection
                if (!response.requiresPayment) {
                    setStep(13); // Move directly to collection step
                } else {
                    // Set payment details and move to payment step
                    setPaymentId(response.assignmentId); 
                    setPaymentQRCode(response.qrCode || '');
                    setPaymentUrl(response.paymentUrl || '');
                    setStep(121); // Payment method selection step
                }
            } else {
                setError(t('invalidOTPError'));
            }
        } catch (error) {
            setError(t('generalError'));
        } finally {
            setIsVerifyingOTP(false); // End loading
        }
    };

    const handlePaymentConfirmation = async () => {
        try {
          if (!assignmentId) {
            setError('No active assignment found.');
            return;
          }
          const success = await confirmPayment(assignmentId);
          if (success) {
            setStep(13); // Move to collection screen after payment
            setIsPollingPayment(false); // Stop polling
          } else {
            setError('Payment confirmation failed. Please try again.');
          }
        } catch (error) {
          setError('An error occurred while confirming payment. Please try again.');
        }
      };

    const handleLockerStateChange = async (isClosed: boolean) => {
        setIsLockerClosed(isClosed);
        if (isClosed && !isCollectionComplete && assignmentId) {
            try {
                // Call API to complete collection when locker is closed
                const responseOk = await completeCollection(assignmentId);
                if (responseOk) {
                    setIsCollectionComplete(true);
                }
            } catch (error) {
                // Error message commented out as requested
                // setError('Error updating collection status');
                console.log('Error occurred during collection completion:', error);
            }
        }
    };
    

    const handleRestart = () => {
        setPhone('');
        setOTP('');
        setError('');
        setStep(0);
        setAmount(0);
        setAssignedLocker('');
    };

    const handlePhoneKeyPress = (key: string) => {
        setPhone(phone + key);
    };

    const handleOTPKeyPress = (key: string) => {
        setOTP(otp + key);
    };

    const renderStep = () => {
        switch (step) {
            case 11:
                return (
                    <>
                        <h2 className="text-xl font-bold text-center text-foreground">{t('enterPhoneNumber')}</h2>
                        <Input
                            type="tel"
                            className="text-foreground"
                            placeholder={t('enterYourPhoneNumber')}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                        <NumericKeypad
                            onKeyPress={handlePhoneKeyPress}
                            onClear={() => setPhone('')}
                            onBackspace={() => setPhone(phone.slice(0, -1))}
                        />
                        <Button onClick={handleCollectionPhoneEnter} variant="outline" size="lg">{t('confirm')}</Button>
                        <Button onClick={handleRestart} variant="outline" size="lg">{t('cancel')}</Button>
                    </>
                );
                case 12:
                    return (
                        <>
                            <h2 className="text-xl font-bold text-center text-foreground">{t('enterOTP')}</h2>
                            {isVerifyingOTP ? (
                                <div className="flex flex-col items-center justify-center space-y-4">
                                    <Loader className="h-8 w-8 animate-spin text-primary" />
                                    <p className="text-foreground text-sm">{t('verifyingOTP')}</p>
                                </div>
                            ) : (
                                <>
                                    <OTPInput value={otp} onChange={setOTP} />
                                    <NumericKeypad
                                        onKeyPress={handleOTPKeyPress}
                                        onClear={() => setOTP('')}
                                        onBackspace={() => setOTP(otp.slice(0, -1))}
                                    />
                                    <Button onClick={handleOTPEnterCollection} variant="outline" size="lg">{t('confirm')}</Button>
                                    <Button onClick={() => setOTP('')} variant="outline" size="lg">{t('resendOTP')}</Button>
                                    <Button onClick={handleRestart} variant="outline" size="lg">{t('cancel')}</Button>
                                </>
                            )}
                        </>
                    );
                    case 121:
                        return (
                            <div className="space-y-4 text-center">
                                <PaymentMethodSelection 
                                    amount={amount}
                                    onPaymentMethodSelected={handlePaymentMethodSelect}
                                    isLoading={isPreparingPayment}
                                />
                                <Button 
                                    onClick={handleRestart}
                                    variant="outline"
                                    size="lg"
                                    className="w-full"
                                >
                                    {t('cancel')}
                                </Button>
                            </div>
                        );
                    
                    // Add a new case for POS terminal payment
                    case 123: // New step for POS terminal payment
                        return (
                            <div className="space-y-4 text-center">
                                <h2 className="text-xl font-bold text-foreground">
                                    {t('posPayment')}
                                </h2>
                                <p className="text-foreground">
                                    {t('amountToPay')} {amount} KM
                                </p>
                                <PaymentTerminal 
                                    amount={amount}
                                    onPaymentComplete={handleTerminalPaymentComplete}
                                />
                                <Button 
                                    onClick={() => setStep(121)} // Go back to payment method selection
                                    variant="outline"
                                    size="lg"
                                    className="w-full"
                                >
                                    {t('back')}
                                </Button>
                                <Button 
                                    onClick={handleRestart}
                                    variant="outline"
                                    size="lg"
                                    className="w-full"
                                >
                                    {t('cancel')}
                                </Button>
                            </div>
                        );
                case 122:
                    return (
                        <div className="space-y-4 text-center">
                            <h2 className="text-xl font-bold text-foreground">
                                {t('paypalPayment')}
                            </h2>
                            <p className="text-foreground">
                                {t('amountToPay')} {amount} KM
                            </p>
                            
                            {paymentQRCode && (
                                <div className="flex justify-center my-4">
                                    <img 
                                        src={`data:image/png;base64,${paymentQRCode}`}
                                        alt="Payment QR Code"
                                        className="w-64 h-64"
                                    />
                                </div>
                            )}
                            
                            <div className="space-y-4">
                                <p className="text-sm text-foreground">
                                    {t('scanQrOrClickBelow')}
                                </p>
                                
                                {isPollingPayment && (
                                    <div className="flex items-center justify-center space-x-2 text-sm text-foreground">
                                        <Loader className="h-4 w-4 animate-spin" />
                                        <span>{t('waitingForPayment')}</span>
                                    </div>
                                )}
                                
                                <Button 
                                    onClick={() => setStep(121)} // Go back to payment method selection
                                    variant="outline"
                                    size="lg"
                                    className="w-full"
                                >
                                    {t('back')}
                                </Button>
                                
                                <Button 
                                    onClick={handleRestart}
                                    variant="outline"
                                    size="lg"
                                    className="w-full"
                                >
                                    {t('cancel')}
                                </Button>
                            </div>
                        </div>
                    );
            case 13:
                return (
                    <div className='space-y-6 space-x-2'>
                        <h2 className="text-xl font-bold text-center text-foreground">
                            {isCollectionComplete ? t('collectionComplete') : t('collectYourParcel')}
                        </h2>
                        <p className="text-foreground">{t('recipient')}: {parcelData?.recipientName}</p>
                        <p className="text-foreground">{t('phone')}: {phone}</p>
                        <p className="text-foreground">{t('assignedLocker')}:</p>
                        <p className='text-3xl font-bold text-foreground'>{assignedLocker}</p>
                        <LockerMonitor 
                            lockerId={assignedLocker} 
                            onStateChange={handleLockerStateChange}
                        />
                        {isLockerClosed && (
                            <Button 
                                onClick={handleRestart} 
                                variant="outline" 
                                size="lg"
                            >
                                {t('finish')}
                            </Button>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-4">
            {renderStep()}
        </div>
    );
};

export default CollectParcel;